import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../utils/datetime';

/** One top-level menu module in the report. */
interface KnowledgeModule {
  module: string;
  screens: number;
  screensWithForm: number;
  fields: number;
  requiredFields: number;
  workflowSteps: number;
}

/** One workflow flow (module + process mode + case), steps in order. */
interface WorkflowChain {
  module: string;
  /** "{process mode} · {case}" — one real flow within the module. */
  flow: string;
  steps: string[];
}

/** GET /api/v1/ava/knowledge/report. */
export interface KnowledgeReport {
  generatedAt: string | null;
  loadProblem: string | null;
  screens: number;
  forms: number;
  fields: number;
  requiredFields: number;
  workflowSteps: number;
  workflowModules: number;
  modules: KnowledgeModule[];
  workflows: WorkflowChain[];
  gaps: string[];
}

const MODULE_COLS: AppColumn<KnowledgeModule>[] = [
  { field: 'module', header: 'Module', minWidth: 160 },
  { field: 'screens', header: 'Screens', type: 'number', width: 90 },
  { field: 'screensWithForm', header: 'With form', type: 'number', width: 100 },
  { field: 'fields', header: 'Fields', type: 'number', width: 80 },
  { field: 'requiredFields', header: 'Required', type: 'number', width: 90 },
  { field: 'workflowSteps', header: 'Workflow steps', type: 'number', width: 120 },
];

const WORKFLOW_COLS: AppColumn<WorkflowChain>[] = [
  { field: 'module', header: 'Module', width: 150 },
  { field: 'flow', header: 'Flow', width: 220, wrap: true },
  { field: 'steps', header: 'Steps (in order)', wrap: true, minWidth: 320,
    value: (r) => r.steps.join(' → '), text: (r) => r.steps.join(' → ') },
];

/**
 * "What AVA knows" (admin) — Step 1 of the AVA training plan: what AVA has learned
 * about this ALPACA installation (screens, form fields, workflow chains) and the
 * known gaps. Read-only; the server refuses non-admins.
 */
export function AvaKnowledgeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [report, setReport] = useState<KnowledgeReport | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setFailed(false);
    api.get('/api/v1/ava/knowledge/report')
      .then((r) => { if (live) setReport(r.data?.data ?? null); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [open]);

  return (
    <AppDialog open={open} onClose={onClose} title="What AVA knows" icon="ti-brain" width={980} maximizable>
      {failed && <div className="ava-know-empty">AVA&apos;s knowledge could not be loaded.</div>}
      {!failed && !report && <div className="ava-know-empty">Loading…</div>}
      {report && (
        <div className="ava-know">
          <div className="ava-know-tiles">
            <div><b>{report.screens}</b><span>screens</span></div>
            <div><b>{report.forms}</b><span>forms</span></div>
            <div><b>{report.fields}</b><span>fields ({report.requiredFields} required)</span></div>
            <div><b>{report.workflowSteps}</b><span>workflow steps · {report.workflowModules} modules</span></div>
          </div>
          {report.generatedAt && (
            <div className="ava-know-sub">Screens and forms as of {fmtLocalDateTime(report.generatedAt)} · workflow read live from the Workflow Status Chain.</div>
          )}
          {report.gaps.length > 0 && (
            <ul className="ava-know-gaps">
              {report.gaps.map((g) => <li key={g}><i className="ti ti-alert-triangle" /> {g}</li>)}
            </ul>
          )}
          <div className="ava-know-h">By module</div>
          <AppDataGrid rows={report.modules} columns={MODULE_COLS} getRowId={(r) => r.module}
            defaultPageSize={10} exportFilename="ava-knowledge-modules" maximizable />
          <div className="ava-know-h">Workflows</div>
          <AppDataGrid rows={report.workflows} columns={WORKFLOW_COLS} getRowId={(r) => `${r.module}|${r.flow}`}
            defaultPageSize={10} exportFilename="ava-knowledge-workflows" maximizable />
        </div>
      )}
    </AppDialog>
  );
}
